import { useMaskito } from "@maskito/react";
import { CalendarIcon } from "@phosphor-icons/react/dist/csr/Calendar";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useField } from "formik";
import React, { useState, useRef, useLayoutEffect } from "react";
import type { Matcher } from "react-day-picker";
import { Calendar } from "@volca/ui/components/ui/calendar";
import { FormInput, FormInputProps } from "@volca/ui/components/ui/form-input";
import { InputGroupButton } from "@volca/ui/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import {
  dayjs,
  FORMAT_TYPEABLE_DATE,
  FORMAT_TYPEABLE_DATE_PLACEHOLDER,
} from "@volca/ui/lib/date";
import { createTypeableDateMask, parseTypeableDate } from "./typeable-date";

export interface TypeableDatePickerProps
  extends Omit<FormInputProps, "value" | "onChange"> {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  name: string;
  fromDate?: Date;
  toDate?: Date;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const TypeableDatePicker: React.FC<TypeableDatePickerProps> = ({
  value,
  onChange,
  label = "Date",
  placeholder = FORMAT_TYPEABLE_DATE_PLACEHOLDER,
  name,
  fromDate,
  toDate,
  error,
  open: controlledOpen,
  onOpenChange,
  ...props
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [inputValue, setInputValue] = useState<string>(
    value ? dayjs(value).format(FORMAT_TYPEABLE_DATE) : "",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState<Date | undefined>(
    value ? dayjs(value).startOf("month").toDate() : undefined,
  );

  const [, meta] = useField(name);

  const validationError =
    error ?? (hasInteracted && meta.error ? meta.error : undefined);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const disabled = React.useMemo((): Matcher[] | undefined => {
    const matchers: Matcher[] = [];
    if (fromDate) {
      matchers.push({ before: fromDate });
    }
    if (toDate) {
      matchers.push({ after: toDate });
    }
    return matchers.length > 0 ? matchers : undefined;
  }, [fromDate, toDate]);

  const defaultEndMonth = React.useMemo(
    () => dayjs().add(12, "year").endOf("year").toDate(),
    [],
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const maskOptions = React.useMemo(() => createTypeableDateMask(), []);
  const maskitoRef = useMaskito({ options: maskOptions });

  const combinedRef = (el: HTMLInputElement | null) => {
    if (el) {
      inputRef.current = el;
      maskitoRef(el);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    setInputValue(val);
    setIsEditing(true);

    const parsed = parseTypeableDate(val);
    if (parsed) {
      onChangeRef.current?.(parsed);
      setIsEditing(false);
    }
  };

  useLayoutEffect(() => {
    if (!isEditing) {
      if (value) {
        setInputValue(dayjs(value).format(FORMAT_TYPEABLE_DATE));
        setVisibleMonth(dayjs(value).startOf("month").toDate());
      } else {
        setInputValue("");
        setVisibleMonth(undefined);
      }
    }
  }, [value, isEditing]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    setHasInteracted(true);
    const val = e.target.value;
    const parsed = parseTypeableDate(val);

    if (val && parsed) {
      onChange?.(parsed);
      return;
    }
    if (val !== "") {
      setInputValue("");
      onChange?.(null);
    }
  };

  return (
    <FormInput
      ref={combinedRef}
      {...props}
      name={name}
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      label={label}
      error={validationError}
      hideErrorMessage={Boolean(error)}
      hideAutomaticError={true}
      aria-invalid={Boolean(validationError)}
      startAdornment={
        <Popover open={open} onOpenChange={(next: boolean) => setOpen(next)}>
          <PopoverTrigger
            render={<InputGroupButton size="icon-xs" />}
            aria-label="Open calendar"
          >
            <CalendarIcon />
          </PopoverTrigger>
          <PopoverContent
            className="w-auto gap-0 overflow-hidden p-0"
            align="start"
            sideOffset={8}
            initialFocus={false}
          >
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={value ?? undefined}
              month={visibleMonth}
              onMonthChange={setVisibleMonth}
              onSelect={(date) => {
                if (date) {
                  onChange?.(date);
                  setInputValue(dayjs(date).format(FORMAT_TYPEABLE_DATE));
                  setHasInteracted(true);
                }
                setIsEditing(false);
                setOpen(false);
              }}
              disabled={disabled}
              startMonth={fromDate}
              endMonth={toDate ?? defaultEndMonth}
              autoFocus={true}
            />
          </PopoverContent>
        </Popover>
      }
      endAdornment={
        value ? (
          <InputGroupButton
            size="icon-xs"
            aria-label="Clear date"
            onClick={() => {
              setInputValue("");
              onChange?.(null);
              setIsEditing(false);
            }}
          >
            <XIcon />
          </InputGroupButton>
        ) : null
      }
    />
  );
};
